import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Search, ShieldAlert, RefreshCcw, Layers, Download } from "lucide-react";

const ACTIONS = [
  "delete_asset", "delete_inspection", "delete_certificate", "delete_schedule",
  "delete_user", "delete_product", "delete_yellow_card", "delete_course",
  "delete_document", "delete_template", "delete_organization",
  "delete_charge", "delete_customer", "deactivate_tenant",
];
const RESOURCE_TYPES = [
  "asset", "inspection", "certificate", "schedule", "user", "product",
  "customer", "organization", "template", "document", "course", "yellow_card", "charge", "tenant",
];

export default function AuditLogs() {
  const { user } = useAuth();
  const canView = ["admin", "approver", "super_admin"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ action: "all", resource_type: "all", actor_email: "", start: "", end: "" });
  const [tenantActivity, setTenantActivity] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [exporting, setExporting] = useState(false);

  const buildParams = (base = {}) => {
    const params = { ...base };
    if (filters.action && filters.action !== "all") params.action = filters.action;
    if (filters.resource_type && filters.resource_type !== "all") params.resource_type = filters.resource_type;
    if (filters.actor_email) params.actor_email = filters.actor_email;
    if (filters.start) params.start = filters.start;
    if (filters.end) params.end = filters.end;
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-logs", { params: buildParams({ limit: 500 }) });
      setRows(data);
    } finally { setLoading(false); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/audit-logs/export", { params: buildParams(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* toast handled below */ }
    finally { setExporting(false); }
  };

  useEffect(() => { if (canView) load(); }, [canView]);
  useEffect(() => {
    if (user?.role !== "super_admin") return;
    setLoadingTenants(true);
    api.get("/tenants-metrics/overview")
      .then(({ data }) => setTenantActivity(data?.per_tenant || []))
      .catch(() => setTenantActivity([]))
      .finally(() => setLoadingTenants(false));
  }, [user]);

  const grouped = useMemo(() => {
    const by = {};
    rows.forEach(r => {
      const day = (r.created_at || "").slice(0, 10) || "—";
      if (!by[day]) by[day] = [];
      by[day].push(r);
    });
    return Object.entries(by).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [rows]);

  if (!canView) {
    return (
      <div className="p-8" data-testid="audit-logs-forbidden">
        <div className="max-w-md mx-auto text-center">
          <ShieldAlert className="w-12 h-12 mx-auto text-[#DC2626] mb-4" />
          <div className="font-display text-xl font-bold">Not authorized</div>
          <p className="text-sm text-gray-600 mt-2">You need admin, approver or super-admin access to view audit logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="audit-logs-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Compliance</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Audit Log</h1>
          <p className="text-xs text-gray-500 mt-2 max-w-xl">
            Every destructive action (delete/deactivate) across your workspace is recorded here — who did it, when,
            from what IP and against which record. {user?.role === "super_admin" && "As super-admin you see all tenants unless impersonating."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="audit-export-btn" onClick={exportCsv} disabled={exporting || rows.length === 0} variant="outline" className="rounded-full">
            <Download className={`w-4 h-4 mr-2 ${exporting ? "animate-pulse" : ""}`} /> Export CSV
          </Button>
          <Button data-testid="audit-refresh-btn" onClick={load} disabled={loading} variant="outline" className="rounded-full">
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Cross-tenant activity — super-admin only */}
      {user?.role === "super_admin" && (
        <div className="mb-6 bg-white border border-gray-200" data-testid="cross-tenant-activity">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-[#01ADEF]" />
              <h2 className="font-display text-lg font-bold tracking-tight">Cross-tenant Activity</h2>
            </div>
            <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-gray-500">
              {tenantActivity.length} workspace(s)
            </span>
          </div>
          {loadingTenants ? (
            <div className="p-6 text-center text-gray-500 text-sm">Loading…</div>
          ) : tenantActivity.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">No cross-tenant activity yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">Tenant</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">Users</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">Equipment</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">Inspections (30d)</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">Certificates</th>
              </tr></thead>
              <tbody data-testid="cross-tenant-table">
                {tenantActivity.map(t => (
                  <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`cross-tenant-row-${t.id}`}>
                    <td className="px-4 py-3 font-semibold">
                      {t.name}
                      {t.is_active === false && <span className="text-[10px] uppercase tracking-wider text-gray-500 ml-2">· inactive</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{t.users}</td>
                    <td className="px-4 py-3 text-right font-mono">{t.assets}</td>
                    <td className="px-4 py-3 text-right font-mono">{t.inspections_30d}</td>
                    <td className="px-4 py-3 text-right font-mono">{t.certificates}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-4 flex flex-wrap items-end gap-4 mb-4" data-testid="audit-filters">
        <div className="w-52">
          <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Action</Label>
          <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
            <SelectTrigger className="mt-1 rounded-md" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {ACTIONS.map(a => <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-52">
          <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Resource Type</Label>
          <Select value={filters.resource_type} onValueChange={(v) => setFilters({ ...filters, resource_type: v })}>
            <SelectTrigger className="mt-1 rounded-md" data-testid="audit-resource-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {RESOURCE_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">From</Label>
          <Input type="date" data-testid="audit-start-filter" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="mt-1 rounded-md" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">To</Label>
          <Input type="date" data-testid="audit-end-filter" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="mt-1 rounded-md" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Actor Email</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input data-testid="audit-actor-filter" value={filters.actor_email} onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })} placeholder="e.g. admin@docq.co.nz" className="pl-9 rounded-md" />
          </div>
        </div>
        <Button data-testid="audit-apply-filters" onClick={load} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">Apply</Button>
      </div>

      {/* Timeline */}
      <div className="bg-white border border-gray-200" data-testid="audit-log-list">
        {loading ? (
          <div className="p-10 text-center text-gray-500 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-500 text-sm">No destructive actions recorded yet.</div>
        ) : (
          <div>
            {grouped.map(([day, items]) => (
              <div key={day} className="border-b border-gray-100 last:border-b-0">
                <div className="px-4 py-2 bg-gray-50 text-xs uppercase tracking-wider font-bold text-gray-600 sticky top-0" data-testid={`audit-day-${day}`}>
                  {day} · {items.length} action(s)
                </div>
                {items.map(r => (
                  <div key={r.id} className="px-4 py-3 border-t border-gray-100 flex items-start gap-4 hover:bg-gray-50" data-testid={`audit-row-${r.id}`}>
                    <div className="w-24 shrink-0 text-xs font-mono text-gray-500">
                      {(r.created_at || "").slice(11, 19)}
                    </div>
                    <div className="w-56 shrink-0">
                      <ActionBadge action={r.action} />
                      <div className="text-xs text-gray-600 mt-1 font-mono">{r.resource_type}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {r.meta?.name || r.meta?.certificate_number || r.meta?.reference_number || r.meta?.title || r.meta?.email || r.resource_id}
                      </div>
                      {r.meta?.cascade && (
                        <div className="text-xs text-[#B91C1C] mt-0.5">
                          Cascade: {Object.entries(r.meta.cascade).map(([k, v]) => `${v} ${k}`).join(" · ")}
                        </div>
                      )}
                      {r.meta?.bulk && <div className="text-[10px] uppercase tracking-wider font-bold text-[#B91C1C] mt-0.5">Bulk operation</div>}
                    </div>
                    <div className="w-48 shrink-0 text-xs">
                      <div className="font-semibold">{r.actor_email}</div>
                      <div className="text-gray-500 uppercase tracking-wider text-[10px]">{r.actor_role}{r.impersonating ? " · impersonating" : ""}</div>
                    </div>
                    <div className="w-32 shrink-0 text-xs text-gray-500 font-mono truncate" title={r.ip || ""}>{r.ip || "—"}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionBadge({ action }) {
  const isDeactivate = action?.startsWith("deactivate");
  const cls = isDeactivate
    ? "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]"
    : "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]";
  return (
    <span className={`inline-block text-xs font-bold px-2 py-0.5 uppercase tracking-wider border ${cls}`}>
      {(action || "").replace(/_/g, " ")}
    </span>
  );
}
